// ────────────────────────────────────────────────────────────────────────────
// Wall-clock timing recorder for the live dex scan (scan-speed 2026-06-30).
//
// WHY THIS EXISTS. `dexScanTelemetry.ts` already answers the latency question
// in FRAME-INDEX space ("first commit landed at frame 74"). That is perfect for
// deterministic tests, but it cannot answer the question a tester actually feels
// on device:
//
//   "I held the phone on a screen for 20-40 SECONDS before more entries
//    applied — where did that real time go: slow OCR, or too-tight gating?"
//
// Frame index is not seconds. A device can spend 18 real seconds across 47
// frames that each OCR'd fine but were all held back by a stability/corroboration
// gate — frame-index telemetry shows "47 frames, healthy holds" and the
// wall-clock pain is invisible. This module is the WALL-CLOCK companion the
// telemetry module's own header invited ("the caller can pair them with
// `lastFrameAt` wall-clock if a millisecond view is wanted").
//
// WHAT IT MEASURES.
//   1. Per-frame compute cost — total ms to process a frame, and the OCR-lane ms
//      (the 5x Tesseract fanout in `extractPokedexFrame`) inside it. Aggregated
//      to mean / p50 / p95 / max so one slow tail frame is visible.
//   2. Commit dwell — the real-time gap (ms) between successive committed
//      entries, and the LONGEST such gap. This is the direct measure of the
//      "holding on the screen" wait: `maxCommitGapMs` is, in seconds, how long
//      the user stared at the screen during the worst stall.
//   3. Idle-vs-busy split — of the wall-clock between the first and last frame,
//      how much was spent inside frame compute vs waiting for the next frame.
//
// It is PURE bookkeeping: no React, no persistence, never affects a write or a
// gate. Framework-agnostic so it is unit-tested deterministically by feeding
// synthetic frame records. `now()` is injectable for tests.
// ────────────────────────────────────────────────────────────────────────────

/** One processed frame's wall-clock record. */
export interface FrameTimingRecord {
  /** Wall-clock ms (performance.now or Date.now) when the frame began processing. */
  startMs: number;
  /** Total ms spent processing this frame (whole `processFrame` body). */
  totalMs: number;
  /**
   * Ms spent in the OCR lane for this frame (the `extractPokedexFrame` fanout),
   * or 0 when the frame was gated/deduped before OCR ran.
   */
  ocrMs: number;
  /** Distinct genuine new-to-dex ids this frame committed (frameApply.newlyAdded). */
  committedCount: number;
  /**
   * Cell number-ROIs this frame batched into its ONE native OCR call. Before the
   * 2026-08-11 batch each ROI was its own native call, so this doubles as the
   * native calls saved on the frame. 0/absent when the frame never reached the
   * cell loop.
   */
  ocrRois?: number;
  /**
   * Ms this frame spent SUSPENDED at a main-thread yield. With two frames in
   * flight a yield lets the OTHER frame run, so this time sits inside `totalMs`
   * without being this frame's work — `totalMs - yieldMs` is the own-work cost,
   * and it is what stays comparable with pre-2026-08-11 traces.
   */
  yieldMs?: number;
  /**
   * The frame was dropped at the pre-OCR blur gate (2026-10-26, scan_perf
   * blur_drops). A blur-dropped frame also carries `ocrMs: 0`; this flag tells
   * it apart from a frame that was deduped or probed as a detail page.
   */
  blurDropped?: boolean;
}

export interface ScanTimingSnapshot {
  /** Number of frames whose processing was timed. */
  framesTimed: number;
  /** Wall-clock ms from the first timed frame's start to the last frame's end. */
  sessionWallMs: number;
  /** Sum of per-frame `totalMs` (busy compute time). */
  busyMs: number;
  /** sessionWallMs - busyMs: time spent idle between frames (waiting on cadence). */
  idleMs: number;
  /** Per-frame total-ms aggregates. */
  frameMs: { mean: number; p50: number; p95: number; max: number };
  /** Per-frame OCR-lane-ms aggregates (over frames where ocrMs > 0). */
  ocrMs: { mean: number; p50: number; p95: number; max: number; frames: number };
  /** Frames the pre-OCR blur gate dropped (FrameTimingRecord.blurDropped). */
  blurDrops: number;
  /** Total distinct entries committed across all timed frames. */
  committed: number;
  /**
   * Cell number-ROIs batched into single native OCR calls. `total` is the whole
   * session's ROI count, `frames` how many frames batched at least one, and
   * `meanPerFrame` the average batch size over those. Read `total - frames` as
   * the native OCR calls this session avoided versus the pre-batch one-call-per-
   * ROI path.
   */
  ocrRois: { total: number; frames: number; meanPerFrame: number; max: number };
  /**
   * Per-frame suspended-at-a-yield aggregates, and `ownWorkMeanMs` — the mean
   * of `totalMs - yieldMs`. Compare THAT against a pre-2026-08-11 frameMeanMs,
   * not the raw mean, which now includes interleaved work from other frames.
   */
  yieldMs: { mean: number; max: number; ownWorkMeanMs: number };
  /**
   * Real-time gaps (ms) between successive commits. `count` is the number of
   * gaps measured (commits after the first), `maxMs` the worst stall, `meanMs`
   * the average wait between commits. When fewer than 2 commits happened the
   * gaps are 0 (no wait to measure).
   */
  commitGapMs: { count: number; meanMs: number; maxMs: number };
  /**
   * Real-time ms from session start to the FIRST commit — the cold-start wait
   * before anything lands. null when nothing committed.
   */
  firstCommitMs: number | null;
}

function quantile(sortedAsc: number[], q: number): number {
  if (sortedAsc.length === 0) return 0;
  if (sortedAsc.length === 1) return sortedAsc[0];
  const idx = q * (sortedAsc.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sortedAsc[lo];
  const frac = idx - lo;
  return sortedAsc[lo] * (1 - frac) + sortedAsc[hi] * frac;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

const TIMING_LOG_PREFIX = "[dex-scan] timing";

/**
 * Create a stateful wall-clock timing recorder for one scan session. Pure
 * bookkeeping; never affects a write or a gate.
 *
 * `log` defaults to `console.log` so the summary also appears in the on-device
 * Xcode/TestFlight trace alongside the existing `[live-scan] finish() done`
 * lines; pass a no-op or capturing fn in tests.
 */
export function createScanTiming(opts?: {
  log?: (event: string, fields: Record<string, unknown>) => void;
}) {
  const log =
    opts?.log ??
    ((event: string, fields: Record<string, unknown>) => {
      // eslint-disable-next-line no-console
      console.log(`${TIMING_LOG_PREFIX} ${event}`, fields);
    });

  const frames: FrameTimingRecord[] = [];
  // Wall-clock timestamps (frame start ms) at which a commit happened, so we can
  // compute real-time gaps between commits independent of frame index.
  const commitTimes: number[] = [];

  return {
    /**
     * Record one processed frame's wall-clock cost. Call once per frame at the
     * end of `processFrame`, regardless of whether the frame committed anything
     * (so idle/busy and gated-frame cost are both captured). A frame that was
     * deduped/gated before OCR passes `ocrMs: 0`.
     */
    noteFrame(rec: FrameTimingRecord): void {
      frames.push(rec);
      if (rec.committedCount > 0) {
        commitTimes.push(rec.startMs);
      }
    },

    snapshot(): ScanTimingSnapshot {
      const framesTimed = frames.length;
      if (framesTimed === 0) {
        return {
          framesTimed: 0,
          sessionWallMs: 0,
          busyMs: 0,
          idleMs: 0,
          frameMs: { mean: 0, p50: 0, p95: 0, max: 0 },
          ocrMs: { mean: 0, p50: 0, p95: 0, max: 0, frames: 0 },
          blurDrops: 0,
          committed: 0,
          ocrRois: { total: 0, frames: 0, meanPerFrame: 0, max: 0 },
          yieldMs: { mean: 0, max: 0, ownWorkMeanMs: 0 },
          commitGapMs: { count: 0, meanMs: 0, maxMs: 0 },
          firstCommitMs: null,
        };
      }

      const firstStart = frames[0].startMs;
      const last = frames[framesTimed - 1];
      const sessionWallMs = Math.max(0, last.startMs + last.totalMs - firstStart);

      const totalsAsc = frames.map((f) => f.totalMs).sort((a, b) => a - b);
      const busyMs = totalsAsc.reduce((s, n) => s + n, 0);
      const ocrAsc = frames
        .map((f) => f.ocrMs)
        .filter((n) => n > 0)
        .sort((a, b) => a - b);
      const ocrSum = ocrAsc.reduce((s, n) => s + n, 0);

      const committed = frames.reduce((s, f) => s + f.committedCount, 0);

      // ROI batching: only frames that actually batched count toward the mean,
      // so a session of gated/deduped frames cannot dilute the batch size.
      const roiFrames = frames.map((f) => f.ocrRois ?? 0).filter((n) => n > 0);
      const roiTotal = roiFrames.reduce((s, n) => s + n, 0);

      const yieldPer = frames.map((f) => f.yieldMs ?? 0);
      const yieldSum = yieldPer.reduce((s, n) => s + n, 0);

      // Gaps between successive commits (real time).
      const gaps: number[] = [];
      for (let i = 1; i < commitTimes.length; i++) {
        gaps.push(Math.max(0, commitTimes[i] - commitTimes[i - 1]));
      }
      const gapSum = gaps.reduce((s, n) => s + n, 0);
      const firstCommitMs =
        commitTimes.length > 0 ? Math.max(0, commitTimes[0] - firstStart) : null;

      return {
        framesTimed,
        sessionWallMs: round1(sessionWallMs),
        busyMs: round1(busyMs),
        idleMs: round1(Math.max(0, sessionWallMs - busyMs)),
        frameMs: {
          mean: round1(busyMs / framesTimed),
          p50: round1(quantile(totalsAsc, 0.5)),
          p95: round1(quantile(totalsAsc, 0.95)),
          max: round1(totalsAsc[totalsAsc.length - 1]),
        },
        ocrMs: {
          mean: ocrAsc.length > 0 ? round1(ocrSum / ocrAsc.length) : 0,
          p50: round1(quantile(ocrAsc, 0.5)),
          p95: round1(quantile(ocrAsc, 0.95)),
          max: ocrAsc.length > 0 ? round1(ocrAsc[ocrAsc.length - 1]) : 0,
          frames: ocrAsc.length,
        },
        blurDrops: frames.reduce((s, f) => s + (f.blurDropped ? 1 : 0), 0),
        committed,
        ocrRois: {
          total: roiTotal,
          frames: roiFrames.length,
          meanPerFrame: roiFrames.length > 0 ? round1(roiTotal / roiFrames.length) : 0,
          max: roiFrames.length > 0 ? Math.max(...roiFrames) : 0,
        },
        yieldMs: {
          mean: round1(yieldSum / framesTimed),
          max: Math.max(0, ...yieldPer),
          ownWorkMeanMs: round1((busyMs - yieldSum) / framesTimed),
        },
        commitGapMs: {
          count: gaps.length,
          meanMs: gaps.length > 0 ? round1(gapSum / gaps.length) : 0,
          maxMs: gaps.length > 0 ? round1(Math.max(...gaps)) : 0,
        },
        firstCommitMs: firstCommitMs == null ? null : round1(firstCommitMs),
      };
    },

    /**
     * Emit the whole-session timing summary to the structured log in one
     * grep-friendly line so a tester can answer "where did the seconds go?" from
     * an Xcode/TestFlight trace. Call once at session end. Pure read; idempotent.
     */
    logSnapshot(): void {
      const s = this.snapshot();
      log("snapshot", {
        framesTimed: s.framesTimed,
        sessionWallMs: s.sessionWallMs,
        busyMs: s.busyMs,
        idleMs: s.idleMs,
        frameMeanMs: s.frameMs.mean,
        frameP95Ms: s.frameMs.p95,
        frameMaxMs: s.frameMs.max,
        ocrMeanMs: s.ocrMs.mean,
        ocrP95Ms: s.ocrMs.p95,
        ocrMaxMs: s.ocrMs.max,
        ocrFrames: s.ocrMs.frames,
        blurDrops: s.blurDrops,
        committed: s.committed,
        // Batch effectiveness (2026-08-11). `roiTotal - roiFrames` is how many
        // native OCR calls the batch saved this session.
        roiTotal: s.ocrRois.total,
        roiFrames: s.ocrRois.frames,
        roiMeanPerFrame: s.ocrRois.meanPerFrame,
        roiMaxPerFrame: s.ocrRois.max,
        // Own-work cost with interleaved yields removed — the number that is
        // comparable with a pre-2026-08-11 frameMeanMs.
        ownWorkMeanMs: s.yieldMs.ownWorkMeanMs,
        yieldMeanMs: s.yieldMs.mean,
        yieldMaxMs: s.yieldMs.max,
        firstCommitMs: s.firstCommitMs,
        commitGapMeanMs: s.commitGapMs.meanMs,
        // The headline number for the "holding on the screen" complaint: the
        // longest real-time stall between two committed entries.
        commitGapMaxMs: s.commitGapMs.maxMs,
      });
    },

    /** Reset between scan sessions. */
    reset(): void {
      frames.length = 0;
      commitTimes.length = 0;
    },
  };
}

export type ScanTiming = ReturnType<typeof createScanTiming>;
